import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { 
  ChevronRight, 
  X, 
  LogOut,
  Sparkles,
  FlaskConical,
  FolderOpen,
  TrendingUp,
  Wand2,
  Zap
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import ChatSessions from './ChatSessions';
import logoLight from '../../assets/logo.png';
import logoDark from '../../assets/logo-dark.png';

// Base navigation items (always visible)
const baseNavigation = [
  { name: 'Agent', href: '/chat', icon: Sparkles },
  { name: 'Models', href: '/models', icon: FlaskConical },
  { name: 'Datasets', href: '/data-sources', icon: FolderOpen },
  { name: 'Dashboards', href: '/dashboards', icon: TrendingUp },
  { name: 'Data Enricher', href: '/data-enhancement', icon: Wand2 },
];

// Feature-flagged navigation items
const featureFlaggedItems = {
  data_transformation: { name: 'Data Transform', href: '/data-transformation', icon: Zap },
};

// Desktop nav item (responsive to collapsed state)
const DesktopNavItem = ({ name, href, icon: Icon, isCollapsed, onChatClick }) => {
  const handleClick = () => {
    // If this is the AI Assistant and sidebar is collapsed, expand it
    if (name === 'Agent' && isCollapsed && onChatClick) {
      onChatClick();
    }
  };

  return (
    <div className="group relative">
      <NavLink
        to={href}
        onClick={handleClick}
        children={({ isActive }) => (
          <div className={`flex items-center rounded-lg p-2 text-sm font-medium transition-all duration-200 transform
            ${isCollapsed ? 'justify-center' : 'px-3 py-2'}
            ${isActive 
              ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white scale-[1.02] shadow-sm' 
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white hover:scale-[1.01]'
            }`}
          >
            <Icon className={`h-5 w-5 flex-shrink-0 transition-transform duration-200 ${!isActive ? 'group-hover:scale-110' : ''} ${!isCollapsed ? 'mr-3' : ''}`} />
            {!isCollapsed && <span className="truncate">{name}</span>}
          </div>
        )}
      />
      
      {/* Tooltip only shows when collapsed */}
      {isCollapsed && (
        <div className="absolute left-full ml-2 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 transform translate-y-[-50%] top-1/2">
          {name}
          <div className="absolute left-[-4px] top-1/2 transform -translate-y-1/2 w-2 h-2 bg-slate-900 rotate-45"></div>
        </div>
      )}
    </div>
  );
};

// Mobile nav item (with text)
const MobileNavItem = ({ name, href, icon: Icon }) => {
  return (
    <div className="group relative">
      <NavLink
        to={href}
        children={({ isActive }) => (
          <div className={`w-full flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 transform
            ${isActive 
              ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white scale-[1.02] shadow-sm' 
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white hover:scale-[1.01] hover:translate-x-1'
            }`}
          >
            <Icon className={`h-5 w-5 mr-3 flex-shrink-0 transition-transform duration-200 ${!isActive ? 'group-hover:scale-110' : ''}`} />
            <span className="truncate">{name}</span>
          </div>
        )}
      />
    </div>
  );
};

function Sidebar({ mobileSidebarOpen, setMobileSidebarOpen }) {
  const { logout } = useAuth();
  const { darkMode } = useTheme();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const { features } = useFeatureFlags(['data_transformation']);

  // Build navigation dynamically based on feature flags
  const navigation = React.useMemo(() => {
    const nav = [...baseNavigation];
    
    // Add feature-flagged items
    if (features.data_transformation) {
      nav.push(featureFlaggedItems.data_transformation);
    }
    
    return nav;
  }, [features]);

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={mobileSidebarOpen} as={React.Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 flex z-40 md:hidden"
          open={mobileSidebarOpen}
          onClose={setMobileSidebarOpen}
        >
          <Transition.Child
            as={React.Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />
          </Transition.Child>
          <Transition.Child
            as={React.Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white border-r border-slate-200 dark:bg-slate-800 dark:border-slate-700">
              <Transition.Child
                as={React.Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full"
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <X className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              
              {/* Logo area */}
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <NavLink to="/chat" className="flex items-center hover:opacity-80 transition-all duration-200 transform hover:scale-105">
                  <img src={darkMode ? logoDark : logoLight} alt="Plexe Logo" className="h-8 w-8 object-contain" />
                  <span className="ml-2 font-semibold text-lg text-slate-800 dark:text-white">Plexe</span>
                </NavLink>
                <button
                  onClick={() => setMobileSidebarOpen(false)}
                  className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors
                           dark:hover:text-white dark:hover:bg-slate-700"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              
              {/* Nav items */}
              <div className="p-3 flex-1 overflow-y-auto">
                <nav className="space-y-1.5">
                  {navigation.map((item) => (
                    <MobileNavItem
                      key={item.name}
                      name={item.name}
                      href={item.href}
                      icon={item.icon}
                    />
                  ))}
                </nav>
              </div>
              
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className={`bg-white border-r border-slate-200 hidden md:block dark:bg-slate-900 dark:border-slate-700 transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-64'}`}>
        <div className="flex flex-col h-full">
          {/* Logo area */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <NavLink
              to="/chat"
              className={`flex items-center transition-all duration-300 hover:opacity-80 transform hover:scale-105 ${isCollapsed ? 'justify-center w-full' : 'gap-3'}`}
            >
              <img src={darkMode ? logoDark : logoLight} alt="Plexe Logo" className="h-8 w-8 object-contain" />
              {!isCollapsed && <span className="font-semibold text-lg text-slate-800 dark:text-white">Plexe</span>}
            </NavLink>
            {!isCollapsed && (
              <button
                onClick={() => setIsCollapsed(true)}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors dark:hover:text-white dark:hover:bg-slate-700"
              >
                <ChevronRight size={16} />
              </button>
            )}
          </div>
          
          {/* Expand button when collapsed */}
          {isCollapsed && (
            <div className="px-2 pb-2">
              <button
                onClick={() => setIsCollapsed(false)}
                className="w-full flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors dark:hover:text-white dark:hover:bg-slate-700"
                title="Expand sidebar"
              >
                <ChevronRight size={16} className="rotate-180" />
              </button>
            </div>
          )}
          
          {/* Nav items */}
          <div className={`py-4 flex-1 overflow-y-auto overflow-x-hidden transition-all duration-300 ${isCollapsed ? 'pl-2 pr-0' : 'pl-3 pr-0'}`}>
            <nav className="space-y-1">
              {navigation.map((item) => (
                <div key={item.name}>
                  <DesktopNavItem
                    name={item.name}
                    href={item.href}
                    icon={item.icon}
                    isCollapsed={isCollapsed}
                    onChatClick={() => setIsCollapsed(false)}
                  />
                  
                  {/* Show chat sessions under AI Assistant when expanded and on chat page */}
                  {item.name === 'Agent' && !isCollapsed && location.pathname === '/chat' && (
                    <ChatSessions />
                  )}
                </div>
              ))}
            </nav>
          </div>
          
          {/* Logout button */}
          <div className={`py-3 border-t border-slate-200 dark:border-slate-700 transition-all duration-300 ${isCollapsed ? 'px-2' : 'px-3'}`}>
            <div className="group relative">
              <button
                onClick={logout}
                className={`w-full flex items-center rounded-lg p-2 text-slate-600 hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors
                  ${isCollapsed ? 'justify-center' : 'gap-3'}`}
              >
                <LogOut size={18} />
                {!isCollapsed && <span>Logout</span>}
              </button>
              
              {/* Logout tooltip - only show when collapsed */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-slate-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 transform translate-y-[-50%] top-1/2">
                  Logout
                  <div className="absolute left-[-4px] top-1/2 transform -translate-y-1/2 w-2 h-2 bg-slate-900 rotate-45"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Sidebar;